import React, { useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Store } from '../../types/Store';
import { getStoreSearchLabel, storeMatchesSearch } from '../../utils/storeHelpers';

interface StoreMultiAutocompleteProps {
    stores: Store[];
    value: Store[];
    onChange: (stores: Store[]) => void;
    label: string;
    loading?: boolean;
    size?: 'small' | 'medium';
    placeholder?: string;
}

const StoreMultiAutocomplete = ({
    stores,
    value,
    onChange,
    label,
    loading = false,
    size = 'small',
    placeholder,
}: StoreMultiAutocompleteProps) => {
    const [search, setSearch] = useState('');

    const allStores = useMemo(() => {
        const uniqueStores = new Map<string, Store>();

        stores.forEach((store) => {
            const id = store.ENTITY_ID?.toString();
            if (id) uniqueStores.set(id, store);
        });

        value.forEach((store) => {
            const id = store.ENTITY_ID?.toString();
            if (id && !uniqueStores.has(id)) uniqueStores.set(id, store);
        });

        return Array.from(uniqueStores.values());
    }, [stores, value]);

    const selectedIds = useMemo(
        () => new Set(value.map((store) => store.ENTITY_ID?.toString()).filter(Boolean)),
        [value]
    );

    const filteredStores = useMemo(() => {
        if (!search) return allStores;
        return allStores.filter((store) => storeMatchesSearch(store, search));
    }, [allStores, search]);

    const toggleStore = (store: Store) => {
        const storeId = store.ENTITY_ID?.toString();
        if (!storeId) return;

        if (selectedIds.has(storeId)) {
            onChange(value.filter((selectedStore) => selectedStore.ENTITY_ID?.toString() !== storeId));
            return;
        }

        onChange([...value, store]);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 0.75,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
                >
                    {label}
                </Typography>
                <TextField
                    fullWidth
                    size={size}
                    placeholder={placeholder}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    inputProps={{ autoComplete: 'off' }}
                    InputProps={{
                        endAdornment: search ? (
                            <IconButton
                                aria-label="Effacer la recherche"
                                size="small"
                                onClick={() => setSearch('')}
                                edge="end"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        ) : null,
                    }}
                    sx={{
                        flex: '1 1 180px',
                        minWidth: 0,
                        '& .MuiInputBase-input': { fontSize: '0.875rem' },
                    }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
                    <CircularProgress size={22} />
                </Box>
            ) : filteredStores.length === 0 ? (
                <Typography color="text.secondary" sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                    Aucun magasin trouvé
                </Typography>
            ) : (
                <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {filteredStores.map((store) => {
                        const storeId = store.ENTITY_ID?.toString();
                        const checked = !!storeId && selectedIds.has(storeId);

                        return (
                            <ListItem
                                key={storeId}
                                onClick={() => toggleStore(store)}
                                sx={{ px: 0.5, py: 0.25, cursor: 'pointer' }}
                            >
                                <Checkbox
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                    sx={{ mr: 1 }}
                                />
                                <ListItemText
                                    primary={getStoreSearchLabel(store)}
                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

export default StoreMultiAutocomplete;
